/**
 * Professional Menu Enhancement
 * - Improved hover/click behavior
 * - Click away to close
 * - Touch device support
 * - Keyboard accessibility
 * - Smooth animations
 */
(function() {
  'use strict';

  // Detect touch device
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Close all open menus
  function closeAllMenus() {
    const navLinks = document.querySelectorAll('.nav-bar__link[aria-haspopup="true"]');
    navLinks.forEach(link => {
      const dropdown = document.getElementById(link.getAttribute('aria-controls'));
      if (dropdown) {
        link.setAttribute('aria-expanded', 'false');
        dropdown.setAttribute('aria-hidden', 'true');
      }
    });
  }

  // Enhanced desktop menu hover behavior
  function initDesktopMenu() {
    const navLinks = document.querySelectorAll('.nav-bar__link[aria-haspopup="true"]');
    let closeTimer = null;
    const HOVER_DELAY = 150;
    const CLOSE_DELAY = 300;

    navLinks.forEach(link => {
      const dropdown = document.getElementById(link.getAttribute('aria-controls'));
      if (!dropdown) return;

      // Mouse enter - open with small delay
      link.addEventListener('mouseenter', () => {
        clearTimeout(closeTimer);
        
        // Close other open menus
        navLinks.forEach(other => {
          if (other !== link) {
            const otherDropdown = document.getElementById(other.getAttribute('aria-controls'));
            if (otherDropdown) {
              other.setAttribute('aria-expanded', 'false');
              otherDropdown.setAttribute('aria-hidden', 'true');
            }
          }
        });
        
        // Open this menu
        setTimeout(() => {
          link.setAttribute('aria-expanded', 'true');
          dropdown.setAttribute('aria-hidden', 'false');
        }, HOVER_DELAY);
      });

      // Mouse leave - close with delay (allows moving to dropdown)
      link.addEventListener('mouseleave', () => {
        closeTimer = setTimeout(() => {
          link.setAttribute('aria-expanded', 'false');
          dropdown.setAttribute('aria-hidden', 'true');
        }, CLOSE_DELAY);
      });

      // Keep open when hovering dropdown
      dropdown.addEventListener('mouseenter', () => {
        clearTimeout(closeTimer);
      });

      dropdown.addEventListener('mouseleave', () => {
        closeTimer = setTimeout(() => {
          link.setAttribute('aria-expanded', 'false');
          dropdown.setAttribute('aria-hidden', 'true');
        }, CLOSE_DELAY);
      });
    });

    // Click away to close - listen on document
    document.addEventListener('click', (e) => {
      const isInsideMenu = e.target.closest('.nav-bar__item') || e.target.closest('.mega-menu') || e.target.closest('.nav-dropdown');
      if (!isInsideMenu) {
        closeAllMenus();
      }
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeAllMenus();
      }
    });
  }

  // Improved mobile menu behavior
  function initMobileMenu() {
    const mobileMenu = document.getElementById('mobile-menu');
    if (!mobileMenu) return;

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const toggle = document.querySelector('[data-action="toggle-menu"]');
        if (toggle && toggle.getAttribute('aria-expanded') === 'true') {
          toggle.click();
        }
      }
    });

    // Smooth scroll to top when opening panels
    const panelButtons = mobileMenu.querySelectorAll('[data-action="open-panel"]');
    panelButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        setTimeout(() => {
          const panel = document.querySelector('.mobile-menu__panel.is-nested.is-active');
          if (panel) {
            panel.scrollTop = 0;
          }
        }, 50);
      });
    });

    // Collapse all when closing mobile menu
    const menuToggle = document.querySelector('.header__mobile-nav-toggle');
    if (menuToggle) {
      menuToggle.addEventListener('click', () => {
        if (menuToggle.getAttribute('aria-expanded') === 'true') {
          // Closing - reset all collapsibles
          setTimeout(() => {
            const collapsibles = mobileMenu.querySelectorAll('[data-action="toggle-collapsible"]');
            collapsibles.forEach(c => {
              c.setAttribute('aria-expanded', 'false');
              const target = document.getElementById(c.getAttribute('aria-controls'));
              if (target) target.style.height = '0';
            });
          }, 300);
        }
      });
    }
  }

  // Keyboard navigation for mega menus
  function initKeyboardNav() {
    const megaMenus = document.querySelectorAll('.mega-menu');
    
    megaMenus.forEach(menu => {
      const links = menu.querySelectorAll('a');
      
      menu.addEventListener('keydown', (e) => {
        const currentIndex = Array.from(links).indexOf(document.activeElement);
        
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const next = links[Math.min(currentIndex + 1, links.length - 1)];
          next?.focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          const prev = links[Math.max(currentIndex - 1, 0)];
          prev?.focus();
        } else if (e.key === 'Home') {
          e.preventDefault();
          links[0]?.focus();
        } else if (e.key === 'End') {
          e.preventDefault();
          links[links.length - 1]?.focus();
        }
      });
    });
  }

  // Highlight current page in menu
  function highlightCurrentPage() {
    const currentPath = window.location.pathname;
    const menuLinks = document.querySelectorAll('.nav-bar__link, .nav-dropdown__link, .mega-menu__link, .mobile-menu__nav-link');
    
    menuLinks.forEach(link => {
      const href = link.getAttribute('href');
      if (href && (href === currentPath || (currentPath.startsWith(href) && href !== '/'))) {
        link.classList.add('is-current');
        link.setAttribute('aria-current', 'page');
        
        // Also highlight parent menu items
        const parentDropdown = link.closest('.nav-dropdown, .mega-menu');
        if (parentDropdown) {
          const parentLink = document.querySelector(`[aria-controls="${parentDropdown.id}"]`);
          if (parentLink) {
            parentLink.classList.add('has-current-child');
          }
        }
      }
    });
  }

  // Add visual indicator for menu items with children
  function addMenuIndicators() {
    const menuItems = document.querySelectorAll('.nav-dropdown__link[aria-haspopup="true"], .mega-menu__title');
    menuItems.forEach(item => {
      if (!item.querySelector('.menu-indicator')) {
        const indicator = document.createElement('span');
        indicator.className = 'menu-indicator';
        indicator.setAttribute('aria-hidden', 'true');
      }
    });
  }

  // Initialize everything
  function init() {
    // Only init desktop menu enhancements on non-touch or when hover trigger is set
    if (!isTouchDevice) {
      initDesktopMenu();
    }
    
    initMobileMenu();
    initKeyboardNav();
    highlightCurrentPage();
    addMenuIndicators();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-section-type="header"]')) {
      init();
    }
  });
})();
